import * as React from 'react';

import { WBIcon, Pressable, WBText, View, ViewProps } from '@/core';
import { StyleSheet } from '@/core/StyleSheet';
import styles from './style';

export interface CheckboxProps {
	/**
	 * Determines whether the checkbox should show as checked.
	 */
	checked?: boolean;
	/**
	 * Determines disabled state for the checkbox. Disables any user interaction when true.
	 */
	isDisabled?: boolean;
	/**
	 * Label giving context to what is selected.
	 */
	label?: string;
	/**
	 * Callback called when the user activates the checkbox. The callback should toggle the given <checked> property to propagate the new state into the component.
	 */
	onChange?: (value: boolean) => void;
	/**
	 * An object to style the outside container of the component.
	 */
	style?: ViewProps['style'];
}

/**
 * Checkboxes are UI elements that allow the user to select options.
 *
 * The checkbox component displays a checkbox with the given <checked> state, accompanied by the given <label>. On click of
 *  either, an event is fired and it's up to the implementer to pass on the new checked state into the component.
 *
 * This component was chosen over the react-native-checkbox component to allow for customised UI and easier state management.
 *
 * Example usecases:
 * - Checkboxes to make a choice of floors and zones on the slots-enabled reservation screen.
 *
 * @example
 * <WBCheckbox label='WBCheckbox example' checked={true} />
 */

export const WBCheckbox: React.FC<CheckboxProps> = ({
	checked = false,
	isDisabled = false,
	label,
	onChange,
	style,
}) => {
	const touchableProps = { disabled: isDisabled, onPress: onChange ? () => onChange(!checked) : undefined };

	const checkboxColor = isDisabled ? StyleSheet.value('$colorDisabled') : StyleSheet.value('$colorBrandQuaternary');
	const labelColor = isDisabled ? StyleSheet.value('$colorDisabled') : undefined;

	return (
		<Pressable {...touchableProps} activeOpacity={0.6}>
			<View style={[styles.content, style]}>
				<WBIcon
					name={checked ? 'checkbox-on' : 'checkbox-off'}
					color={checkboxColor}
					size={20}
					style={styles.checkbox}
				/>
				<WBText style={styles.label} color={labelColor}>
					{label}
				</WBText>
			</View>
		</Pressable>
	);
};
